import { useState } from 'react';
import type { Room, RoomMember, User } from '../types';
import { api } from '../api/client';
import { useRoomsStore, useAuthStore } from '../store';

interface GroupInfoProps {
  room: Room;
  onClose: () => void;
  onUpdated: (room: Room) => void;
}

export function GroupInfo({ room, onClose, onUpdated }: GroupInfoProps) {
  const { user } = useAuthStore();
  const { upsertRoom } = useRoomsStore();
  const [members, setMembers] = useState<RoomMember[]>(room.members || []);
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState<User[]>([]);
  const [, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const myRole = members.find(m => m.user_id === user?.id)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const handleSearch = async (q: string) => {
    setAddSearch(q);
    if (q.length < 2) { setAddResults([]); return; }
    setSearching(true);
    try {
      const { users } = await api.users.search(q);
      const existing = new Set(members.map(m => m.user_id));
      setAddResults(users.filter(u => !existing.has(u.id)));
    } finally { setSearching(false); }
  };

  const handleAdd = async (userId: string) => {
    setAdding(userId);
    try {
      await api.rooms.addMember(room.id, userId);
      const { room: updated } = await api.rooms.get(room.id);
      setMembers(updated.members || []);
      upsertRoom(updated);
      onUpdated(updated);
      setAddSearch('');
      setAddResults([]);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal menambah member');
    } finally { setAdding(null); }
  };

  const handleRemove = async (userId: string) => {
    if (!confirm('Hapus anggota ini dari grup?')) return;
    setRemoving(userId);
    try {
      await api.rooms.removeMember(room.id, userId);
      setMembers(prev => prev.filter(m => m.user_id !== userId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal menghapus member');
    } finally { setRemoving(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
      <div
        className="glass rounded-3xl w-full max-w-md shadow-2xl animate-slide-up overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative h-32 bg-gradient-to-br from-purple-600 to-pink-600 flex items-end p-5">
          <div className="absolute inset-0 bg-black/20" />
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 bg-black/30 rounded-xl text-white hover:bg-black/50 transition-all">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="relative flex items-end gap-3">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl font-bold text-white border border-white/30">
              {(room.name || 'G')[0]}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{room.name}</h2>
              <p className="text-white/70 text-sm">{members.length} anggota</p>
            </div>
          </div>
        </div>

        <div className="p-5 max-h-[50vh] overflow-y-auto space-y-4">
          {room.description && (
            <p className="text-dark-300 text-sm">{room.description}</p>
          )}

          {/* Add member (admins only) */}
          {canManage && (
            <div className="space-y-2">
              <label className="text-xs text-dark-400 font-medium uppercase tracking-wider">Tambah Anggota</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Cari pengguna..."
                  value={addSearch}
                  onChange={e => handleSearch(e.target.value)}
                  className="input-base text-sm"
                />
              </div>
              {addResults.map(u => (
                <div key={u.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/[0.04] transition-all">
                  <img src={u.avatar_url || undefined} alt="" className="w-8 h-8 rounded-full" />
                  <span className="flex-1 text-sm text-white truncate">{u.name}</span>
                  <button
                    onClick={() => handleAdd(u.id)}
                    disabled={adding === u.id}
                    className="px-3 py-1 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg transition-all disabled:opacity-50"
                  >
                    {adding === u.id ? '…' : 'Tambah'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Member list */}
          <div className="space-y-1">
            <label className="text-xs text-dark-400 font-medium uppercase tracking-wider">Anggota</label>
            {members.map(m => (
              <div key={m.user_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/[0.03] transition-all">
                {m.user?.avatar_url ? (
                  <img src={m.user.avatar_url} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white font-bold">
                    {(m.user?.name || '?')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {m.user?.name}
                    {m.user_id === user?.id && <span className="text-dark-400"> (Anda)</span>}
                  </p>
                  <p className="text-dark-500 text-xs">{m.role}</p>
                </div>
                {canManage && m.user_id !== user?.id && m.role !== 'owner' && (
                  <button
                    onClick={() => handleRemove(m.user_id)}
                    disabled={removing === m.user_id}
                    className="p-1.5 text-dark-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
